// ============================================================
// Star Line — Premium Custom Select
// Glass-style dropdown with spring animations
// ============================================================

import { Fragment, useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';

interface SelectOption {
  value: string;
  label: string;
  /**
   * Optional heading this option sits under, the equivalent of `<optgroup>`.
   * Groups render in the order they first appear; options with no group come
   * first, ungrouped. Set on none of the options and the list is flat, exactly
   * as before.
   */
  group?: string;
}

interface CustomSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  icon?: React.ReactNode;
  title?: string;
  placeholder?: string;
  /** 'default' = SearchForm hero, 'compact' = admin/filter bar inline */
  variant?: 'default' | 'compact';
  className?: string;
}

export default function CustomSelect({
  options,
  value,
  onChange,
  icon,
  title,
  placeholder = 'Select…',
  variant = 'default',
  className,
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * Which way the list opens. A compact trigger in a toolbar is usually near
   * the right edge, and a `min-w` list anchored to its left edge runs off the
   * window. Measured, so it flips to the edge it fits on.
   */
  const [placement, setPlacement] = useState({ alignRight: false, above: false });

  useLayoutEffect(() => {
    if (!isOpen) return;
    const trigger = containerRef.current?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    if (!trigger || !menu) return;
    const margin = 8;
    setPlacement({
      alignRight:
        trigger.left + menu.width > window.innerWidth - margin
        && trigger.right - menu.width > margin,
      above:
        trigger.bottom + menu.height > window.innerHeight - margin
        && trigger.top - menu.height > margin,
    });
  }, [isOpen]);

  /**
   * The options in the order they are drawn — ungrouped first, then each group
   * in order of first appearance. Keyboard navigation walks *this*, so arrowing
   * down always moves to the row visually below.
   */
  const ordered = useMemo(() => {
    if (!options.some(o => o.group)) return options;
    const groups: string[] = [];
    for (const option of options) {
      const key = option.group ?? '';
      if (!groups.includes(key)) groups.push(key);
    }
    return groups.flatMap(key => options.filter(o => (o.group ?? '') === key));
  }, [options]);

  const selectedLabel = ordered.find(o => o.value === value)?.label || placeholder;
  const selectedIndex = ordered.findIndex(o => o.value === value);

  // ── Close on outside click ───────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // ── Scroll highlighted item into view ────────────────────
  // Addressed by data attribute rather than child index: with group headings in
  // the list, the nth child is not the nth option.
  useEffect(() => {
    if (!isOpen || highlightIndex < 0 || !listRef.current) return;
    const item = listRef.current.querySelector(`[data-option-index="${highlightIndex}"]`);
    if (item) (item as HTMLElement).scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, isOpen]);

  // Reset highlight when opening
  useEffect(() => {
    if (isOpen) {
      setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [isOpen, selectedIndex]);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const select = useCallback((val: string) => {
    onChange(val);
    setIsOpen(false);
  }, [onChange]);

  // ── Keyboard navigation ──────────────────────────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightIndex >= 0 && ordered[highlightIndex]) {
          select(ordered[highlightIndex].value);
        } else {
          setIsOpen(true);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightIndex(prev => Math.min(prev + 1, ordered.length - 1));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightIndex(prev => Math.max(prev - 1, 0));
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  }, [isOpen, highlightIndex, ordered, select]);

  const isCompact = variant === 'compact';

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      {/* ── Trigger ─────────────────────────────────────── */}
      <button
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={title}
        title={title}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        className={isCompact
          ? `flex items-center gap-2 bg-secondary/40 border border-border/30 text-foreground rounded-lg text-sm cursor-pointer transition-all duration-150
             focus:outline-none focus:ring-2 focus:ring-primary/20 hover:bg-secondary/70 appearance-none
             h-9 px-3
             ${isOpen ? 'ring-2 ring-primary/20 bg-secondary/70' : ''}`
          : `w-full bg-secondary text-foreground rounded-lg pl-10 pr-9 py-3 text-base font-medium text-left cursor-pointer transition-all duration-150
             focus:outline-none focus:ring-2 focus:ring-primary/50 hover:bg-secondary/80
             ${isOpen ? 'ring-2 ring-primary/50 bg-secondary/80' : ''}`
        }
      >
        {/* Left icon */}
        {icon && !isCompact && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2">
            {icon}
          </span>
        )}

        {/* Selected value */}
        <span className={isCompact ? 'truncate' : 'block truncate'}>{selectedLabel}</span>

        {/* Animated Chevron */}
        <motion.span
          className={isCompact
            ? 'flex items-center justify-center ml-auto pl-1'
            : 'absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center'
          }
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25, mass: 0.5 }}
        >
          <ChevronDown
            className={`w-3.5 h-3.5 transition-colors duration-150 ${
              isOpen ? 'text-primary' : 'text-muted-foreground/50'
            }`}
          />
        </motion.span>
      </button>

      {/* ── Dropdown ────────────────────────────────────── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30, mass: 0.35 }}
            style={{ transformOrigin: placement.above ? 'bottom' : 'top' }}
            className={`absolute z-50 ${placement.above ? 'bottom-full mb-1.5' : 'top-full mt-1.5'}
              ${isCompact
                ? `min-w-[180px] ${placement.alignRight ? 'right-0' : 'left-0'}`
                : placement.alignRight ? 'right-0 min-w-full' : 'left-0 right-0'}
              bg-card/95 backdrop-blur-xl border border-border/40 rounded-xl
              shadow-[0_8px_30px_-4px_rgba(0,0,0,0.35),0_2px_8px_-2px_rgba(0,0,0,0.2)]
              overflow-hidden`}
          >
            <ul
              ref={listRef}
              role="listbox"
              className="max-h-56 overflow-y-auto py-1"
              style={{ scrollbarWidth: 'thin', scrollbarColor: 'hsl(var(--muted-foreground) / 0.15) transparent' }}
            >
              {ordered.map((opt, i) => {
                const isSelected = opt.value === value;
                const isHighlighted = i === highlightIndex;
                // A heading whenever the group changes — the `<optgroup>` role,
                // without leaving the styled list.
                const heading = opt.group && opt.group !== ordered[i - 1]?.group
                  ? opt.group
                  : null;
                return (
                  // A fragment, not a wrapper element: a <ul> may only contain
                  // <li>, and a stray <div> would also break the listbox role.
                  <Fragment key={opt.value}>
                  {heading && (
                    <li
                      role="presentation"
                      className={`px-3.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 ${
                        i === 0 ? 'pt-1' : 'pt-2.5'
                      }`}
                    >
                      {heading}
                    </li>
                  )}
                  <motion.li
                    role="option"
                    data-option-index={i}
                    aria-selected={isSelected}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.02, 0.2), duration: 0.15, ease: 'easeOut' }}
                    onMouseEnter={() => setHighlightIndex(i)}
                    onClick={() => select(opt.value)}
                    className={`flex items-center justify-between py-2 text-sm cursor-pointer transition-colors duration-75 mx-1 rounded-lg
                      ${opt.group ? 'pl-5 pr-3.5' : 'px-3.5'}
                      ${isHighlighted
                        ? 'bg-primary/8 text-foreground'
                        : 'text-foreground/75'
                      }
                      ${isSelected ? 'font-semibold text-foreground' : 'font-medium'}
                    `}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isSelected && (
                      <motion.span
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                        className="flex-shrink-0 ml-3"
                      >
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </motion.span>
                    )}
                  </motion.li>
                  </Fragment>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
